// Accessible error messages for every calculator (WCAG 1.3.1, 3.3.1, 4.1.3).
//
// The calculators show validation errors by inserting a <span class="error">
// (or "error-validation") straight after the field and removing it again once
// the value is valid (common/js/calculator.js and each calculator's own
// checks). Those messages were only visual: not tied to the field, not
// announced, and focus stayed on the Next button.
//
// This module watches the calculators for those spans, so no calculator has to
// change:
//   - an error that appears links itself to its field (aria-describedby +
//     aria-invalid="true"); when it goes, both are removed again;
//   - if the visitor had just pressed a button in that calculator (e.g. Next),
//     focus moves to the first field with an error, so a screen reader reads
//     the field and its message together;
//   - otherwise the message is announced through a polite live region.
// Nothing visible changes: the messages look and behave exactly as before.
//
// Errors show one at a time, as the calculators intend: an empty field says
// "This Field is Required"; a value out of range gets the calculator's own
// message, which already states the valid range ("Age must be between 18 to
// 60"). Only the vague "Phone Number is not Correct" gets the expected format
// appended (WCAG 3.3.3 Error Suggestion).
//
// It also makes sure every calculator field has a name (WCAG 1.3.1, 3.3.2,
// 4.1.2): most calculators move the field inside its label sentence ("I am
// {age} years old"); a field that is not inside a label is linked to the label
// of its own field group with aria-labelledby.
const CALCULATORS = ".chli__calculator--container, .ppf__calculator--wrapper, .bmi-calculator";
const ERROR = "error";
const ERROR_ALT = "error-validation";
const FIELD = "input, select, textarea";

// Same rules the calculators validate against (see each calculator's
// validateAllInputFields / validatePhoneNumber).
const HINTS = [
    { field: ".term-insurance--input-age", hint: "Enter an age from 18 to 60." },
    { field: ".term-insurance--input-monthly", hint: "Enter a monthly income of \u20b95,000 or more." },
    { field: ".retirement--input-current-age", hint: "Enter an age from 18 to 59." },
    { field: ".retirement--input-retirement-age", hint: "Enter an age from 55 to 75." },
    { field: "[class*='--input-contact']", hint: "Enter a 10-digit mobile number starting with 6, 7, 8 or 9." },
];

let idCounter = 0;
let liveRegion;

function isErrorNode(node) {
    return node.nodeType === 1 && (node.classList.contains(ERROR) || node.classList.contains(ERROR_ALT));
}

function fieldFor(errorEl) {
    const field = errorEl.previousElementSibling;
    return field && field.matches(FIELD) ? field : null;
}

function describedByWithout(field, id) {
    return (field.getAttribute("aria-describedby") || "").split(/\s+/).filter((token) => token && token !== id);
}

function addHint(errorEl, field) {
    const rule = HINTS.find((item) => field.matches(item.field));
    const text = errorEl.textContent.trim();
    if (rule && text && text.indexOf(rule.hint) === -1 && !/required|between|more than|greater than/i.test(text)) {
        errorEl.textContent = `${text.replace(/[.\s]*$/, "")}. ${rule.hint}`;
    }
}

function link(errorEl) {
    const field = fieldFor(errorEl);
    if (!field) {
        return null;
    }
    addHint(errorEl, field);
    if (!errorEl.id) {
        idCounter += 1;
        errorEl.id = `calc-error-${idCounter}`;
    }
    field.setAttribute("aria-describedby", describedByWithout(field, errorEl.id).concat(errorEl.id).join(" "));
    field.setAttribute("aria-invalid", "true");
    field.dataset.calcErrorId = errorEl.id;
    return field;
}

function unlink(field) {
    const id = field.dataset.calcErrorId;
    if (!id || document.getElementById(id)) {
        return;
    }
    const rest = describedByWithout(field, id);
    if (rest.length) {
        field.setAttribute("aria-describedby", rest.join(" "));
    } else {
        field.removeAttribute("aria-describedby");
    }
    field.removeAttribute("aria-invalid");
    delete field.dataset.calcErrorId;
}

function announce(text) {
    if (!liveRegion) {
        liveRegion = document.createElement("div");
        liveRegion.className = "visually-hidden";
        liveRegion.setAttribute("aria-live", "polite");
        liveRegion.setAttribute("aria-atomic", "true");
        document.body.appendChild(liveRegion);
    }
    liveRegion.textContent = "";
    // A fresh text node after clearing makes screen readers repeat a message.
    window.setTimeout(() => {
        liveRegion.textContent = text;
    }, 50);
}

function hasName(field) {
    return (field.labels && field.labels.length) || field.hasAttribute("aria-label") || field.hasAttribute("aria-labelledby");
}

function nameFields(scope) {
    const fields = scope.matches && scope.matches(FIELD) ? [scope] : Array.prototype.slice.call(scope.querySelectorAll ? scope.querySelectorAll(FIELD) : []);
    fields.forEach((field) => {
        if (field.type === "hidden" || hasName(field)) {
            return;
        }
        // Look only in the field's own small group (wrapper span and its
        // parent), so a field is never named after another field's label.
        let group = field.parentElement;
        let label = null;
        for (let depth = 0; depth < 2 && group && !label && !group.matches(CALCULATORS); depth++) {
            const labels = group.querySelectorAll("label");
            label = labels.length === 1 ? labels[0] : null;
            group = group.parentElement;
        }
        if (!label || !label.textContent.trim()) {
            return;
        }
        if (!label.id) {
            idCounter += 1;
            label.id = `calc-label-${idCounter}`;
        }
        field.setAttribute("aria-labelledby", label.id);
    });
}

function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
}

function handleNewErrors(root, fields) {
    const active = document.activeElement;
    const pressedButton = active && root.contains(active) && active.matches("button, [type=submit], [role=button], a");
    const first = Array.prototype.find.call(
        root.querySelectorAll('[aria-invalid="true"]'),
        isVisible
    );
    if (pressedButton && first) {
        first.focus();
        return;
    }
    const messages = fields
        .map((field) => document.getElementById(field.dataset.calcErrorId))
        .filter(Boolean)
        .map((el) => el.textContent.trim())
        .filter(Boolean);
    if (messages.length) {
        announce(messages.join(". "));
    }
}

function watch(root) {
    let pending = [];
    let queued = false;
    const observer = new MutationObserver((records) => {
        records.forEach((record) => {
            record.addedNodes.forEach((node) => {
                if (node.nodeType === 1) {
                    nameFields(node);
                }
                if (isErrorNode(node)) {
                    const field = link(node);
                    if (field) {
                        pending.push(field);
                    }
                }
            });
            record.removedNodes.forEach((node) => {
                if (isErrorNode(node)) {
                    const field = root.querySelector(`[data-calc-error-id="${node.id}"]`);
                    if (field) {
                        unlink(field);
                    }
                }
            });
        });
        if (pending.length && !queued) {
            queued = true;
            // Let the calculator finish its own validation pass first.
            window.setTimeout(() => {
                queued = false;
                const fields = pending.filter((field) => field.getAttribute("aria-invalid") === "true");
                pending = [];
                if (fields.length) {
                    handleNewErrors(root, fields);
                }
            }, 0);
        }
    });
    observer.observe(root, { childList: true, subtree: true });
    nameFields(root);
}

const roots = Array.prototype.slice.call(document.querySelectorAll(CALCULATORS));
// Watch only the outermost calculator element, so nothing is handled twice.
roots.filter((root) => !roots.some((other) => other !== root && other.contains(root))).forEach(watch);
